import { isLoggedIn, type ServiceError } from "@shapr3d/frontend-lib";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { UMSrequestClient } from "../stores/requestClient";
import type { Brand } from "../utils/typeUtils";
import {
  type AccountData,
  type AccountResponseData,
  type Subscription,
  SubscriptionPeriod,
  SubscriptionTier,
  SubscriptionType,
} from "./useAccountTypes";

export const accountQueryKey = ["account"];

export type UserID = Brand<string, "UserID">;

const accountResponseTransformer = ({ data }: { data: AccountResponseData }): AccountData => {
  const subscriptions = data.subscriptions.map(
    (subscription): Subscription => ({
      ...subscription,
      expiration: new Date(subscription.expiration),
      gracePeriodExpirationDate: subscription.gracePeriodExpirationDate
        ? new Date(subscription.gracePeriodExpirationDate)
        : undefined,
    })
  );

  const hasAppStoreSubscription = subscriptions.some(
    (subscription) => subscription.type === SubscriptionType.AppStore
  );
  const hasPaddleSubscription = subscriptions.some(
    (subscription) => subscription.type === SubscriptionType.Paddle
  );
  const hasChargebeeSubscription = subscriptions.some(
    (subscription) => subscription.type === SubscriptionType.Chargebee
  );
  const subscriptionInGracePeriod = subscriptions.find(
    (subscription) =>
      Boolean(subscription.gracePeriodExpirationDate) &&
      subscription.gracePeriodExpirationDate &&
      subscription.gracePeriodExpirationDate > subscription.expiration
  );

  const canPurchaseSubscription = () => {
    if (!isLoggedIn() || hasPaddleSubscription || hasChargebeeSubscription) {
      return false;
    }

    if (hasAppStoreSubscription) {
      const hasAppStoreWithAutoRenewal = data.subscriptions.some(
        (subscription) =>
          subscription.type === SubscriptionType.AppStore && subscription.autorenewal
      );
      return !hasAppStoreWithAutoRenewal;
    }

    return true;
  };

  let teamSubscriptions: Subscription[] = [];
  if (data.teamSubscription && data.teamSubscription.expirationDate) {
    const subscription: Subscription = {
      teamId: data.teamSubscription.id,
      type: SubscriptionType.Team,
      expiration: new Date(data.teamSubscription.expirationDate),
      autorenewal: false,
      isTrial: false,
      subscriptionPeriod: SubscriptionPeriod.Yearly,
      teamName: data.teamSubscription.name,
      tier: SubscriptionTier.Pro,
    };
    teamSubscriptions = [subscription];
  }

  const hasTeamSubscription = teamSubscriptions.length > 0;

  const techTrialAvailable =
    canPurchaseSubscription() &&
    data.subscriptions.length === 1 &&
    data.subscriptions.some((sub) => sub.type === SubscriptionType.AppStore) &&
    data.availablePlans.length > 0 &&
    !hasTeamSubscription;

  // removes teamSubscription property from the data object
  const { teamSubscription: _, ...dataWithoutTeamSubscription } = data;

  return {
    ...dataWithoutTeamSubscription,
    subscriptions: subscriptions.concat(teamSubscriptions),
    hasPaddleSubscription,
    hasChargebeeSubscription,
    hasAppStoreSubscription,
    subscriptionInGracePeriod,
    canPurchaseSubscription,
    techTrialAvailable,
  } satisfies AccountData;
};

export default function useAccount(
  { enabled } = { enabled: true }
): UseQueryResult<AccountData, ServiceError> {
  return useQuery<AccountResponseData, ServiceError, AccountData, typeof accountQueryKey>({
    queryKey: accountQueryKey,
    queryFn: async ({ signal }) => {
      const response = await UMSrequestClient.authenticatedRequest({
        method: "GET",
        url: "/account",
        abortSignal: signal,
      });
      return response.json<AccountResponseData>();
    },
    enabled: isLoggedIn() && enabled,
    staleTime: Infinity,
    retry: 3, // Retry to avoid preflight CORS issues around UMC and WebViewer (COL2-97)
    retryDelay: 100,
    select: (data) => accountResponseTransformer({ data }),
  });
}
